import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import SectionHead from '../components/SectionHead';
import KnobFinder from '../components/KnobFinder';
import Reveal from '../components/Reveal';
import BlueprintOverlay from '../components/BlueprintOverlay';
import { BTN_PRIMARY, BTN_SECONDARY } from '../components/styles';
import { PRODUCTS, PRODUCT_BUCKETS } from '../content/products';
import usePageMeta from '../hooks/usePageMeta';

export default function Products() {
  usePageMeta(
    'Commercial Stove Safety Knob Products | RangeSafe',
    'Explore Classic, Compact, Deluxe and Ultimate stove safety knobs for commercial buildings. Compare 18 models and find the right fit for your stoves.'
  );
  const [tableOpen, setTableOpen] = useState(false);
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHead
            as="h1"
            eyebrow="The Line"
            lines={['Explore RangeSafe Products']}
            lead="RangeSafe provides a collection of stove safety products designed to meet various preferences and stove specifications. Classic and Compact knobs use the Push, Press and Turn™ action. Specialty knobs feature additional Ultimate locking mechanisms and the larger Pro-Style Deluxe bodies."
          />
          <Reveal delay={0.1}>
            <Link to="/products#compare" className={BTN_SECONDARY} data-testid="products-compare-cta">
              Compare Products
            </Link>
          </Reveal>
        </div>
      </header>

      <div className="mx-auto max-w-shell space-y-20 px-5 pb-8 sm:px-8">
        {PRODUCT_BUCKETS.map((bucket) => {
          const items = PRODUCTS.filter((p) => p.bucket === bucket.name);
          const groups = bucket.groups || [null];
          return (
            <section key={bucket.name} id={bucket.name.toLowerCase()} className="scroll-mt-24" data-testid={`bucket-${bucket.name.toLowerCase()}`}>
              <Reveal>
                <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
                  {bucket.name}
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-dim sm:text-base">{bucket.blurb}</p>
              </Reveal>
              {groups.map((group) => {
                const groupItems = items.filter((p) => (p.group || null) === group);
                return (
                  <div key={group || bucket.name} className="mt-10">
                    {group && (
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute" data-testid={`group-${group.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                        {group}
                      </p>
                    )}
                    <div className="mt-4 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
                      {groupItems.map((p, i) => (
                        <Reveal key={p.slug} delay={Math.min(i * 0.04, 0.2)} className="bg-base">
                          <Link
                            to={`/products/${p.slug}`}
                            className="group flex h-full flex-col p-7 transition-colors hover:bg-panel"
                            data-testid={`product-card-${p.slug}`}
                          >
                            <div className="spec-frame relative flex h-52 items-center justify-center overflow-hidden border border-line2">
                              <BlueprintOverlay product={p} />
                              {p.image ? (
                                <img
                                  src={p.image}
                                  alt={`RangeSafe ${p.name} Stove Safety Knob`}
                                  className="relative max-h-44 w-auto object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                                  width="768"
                                  height="1024"
                                  loading="lazy"
                                />
                              ) : null}
                            </div>
                            <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.25em] text-accentText">
                              {p.family} · {p.size}
                            </p>
                            <h3 className="mt-2 font-display text-xl font-bold uppercase leading-tight tracking-tight text-white">
                              {p.name}
                            </h3>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-mute">{p.finish}</p>
                            <p className="mt-4 text-sm leading-relaxed text-dim">{p.tagline}</p>
                          </Link>
                        </Reveal>
                      ))}
                      {Array.from({ length: (2 - (groupItems.length % 2)) % 2 }).map((_, i) => (
                        <div key={`filler-sm-${i}`} aria-hidden="true" className="hidden bg-base sm:block lg:hidden" />
                      ))}
                      {Array.from({ length: (3 - (groupItems.length % 3)) % 3 }).map((_, i) => (
                        <div key={`filler-lg-${i}`} aria-hidden="true" className="hidden bg-base lg:block" />
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>

      <section id="compare" className="mx-auto max-w-shell scroll-mt-24 px-5 py-24 sm:px-8 lg:py-32" data-testid="comparison">
        <SectionHead eyebrow="Comparison" lines={['Compare the models']} />
        <Reveal delay={0.1}>
          <div id="finder" className="mt-12 scroll-mt-24" data-testid="finder">
            <KnobFinder />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14" data-testid="comparison-collapse">
            <button
              type="button"
              onClick={() => setTableOpen((v) => !v)}
              aria-expanded={tableOpen}
              aria-controls="comparison-table-region"
              data-testid="comparison-toggle"
              className="flex w-full items-center justify-between gap-4 border border-line bg-panel/40 px-6 py-5 text-left transition-colors hover:bg-panel"
            >
              <span className="min-w-0">
                <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-accentText">Full table</span>
                <span className="mt-1 block font-display text-base font-bold uppercase tracking-tight text-white sm:text-lg">
                  View all {PRODUCTS.length} models side by side
                </span>
              </span>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-mute transition-transform duration-300 ${tableOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            <AnimatePresence initial={false}>
              {tableOpen && (
                <motion.div
                  id="comparison-table-region"
                  key="comparison-table"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                  data-testid="comparison-table-region"
                >
                  <div className="overflow-x-auto border border-t-0 border-line" data-testid="comparison-table-wrap">
                    <table className="w-full min-w-[980px] border-collapse text-left text-sm" data-testid="comparison-table">
                      <caption className="sr-only">RangeSafe stove safety knob model comparison</caption>
                      <thead>
                        <tr className="border-b border-line bg-panel font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
                          <th scope="col" className="px-5 py-4 font-medium">Bucket</th>
                          <th scope="col" className="px-5 py-4 font-medium">Model</th>
                          <th scope="col" className="px-5 py-4 font-medium">Family</th>
                          <th scope="col" className="px-5 py-4 font-medium">Size</th>
                          <th scope="col" className="px-5 py-4 font-medium">Finish</th>
                          <th scope="col" className="px-5 py-4 font-medium">Locking component</th>
                          <th scope="col" className="px-5 py-4 font-medium">Mechanism</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PRODUCTS.map((p) => (
                          <tr key={p.slug} className="border-b border-line2 transition-colors last:border-0 hover:bg-panel">
                            <td className="px-5 py-4 text-dim">{p.bucket}{p.group ? ` · ${p.group}` : ''}</td>
                            <th scope="row" className="px-5 py-4 font-medium text-white">
                              <Link to={`/products/${p.slug}`} className="hover:text-accent2" data-testid={`comparison-link-${p.slug}`}>
                                {p.name}
                              </Link>
                            </th>
                            <td className="px-5 py-4 text-dim">{p.family}</td>
                            <td className="px-5 py-4 text-dim">{p.size}</td>
                            <td className="px-5 py-4 text-dim">{p.finish}</td>
                            <td className="px-5 py-4 text-dim">{p.locking ? 'Ultimate locking' : 'Standard locking'}</td>
                            <td className="px-5 py-4 text-dim">{p.mechanism}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-6 max-w-2xl text-xs leading-relaxed text-mute">
                    Not every family, size and finish combination is offered — the table lists the
                    actual line. Commercial pricing is quote-based: request a consultation for
                    portfolio pricing.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
            <Link to="/quote" className={`${BTN_PRIMARY} mt-8`} data-testid="products-quote-cta">
              Request a Consultation
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
